import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Switch, Platform, Image } from 'react-native';
import { Link } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import { useProject } from '../lib/ProjectContext';
import type { Category, ProjectKeyword } from '../lib/database.client.types';
import { isProjectActivated } from '../lib/projectActivation';
import colors from '../lib/colors';
import { fonts, interSemiBold, interMedium, interRegular } from "../lib/fonts";
import { getSourceLogoById, getSourceLogoByName } from '../lib/sourceLogos';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';

interface ProjectDetailsCardProps {
    style?: any;
    mode?: 'configuration' | 'description';
    visibility?: 'private' | 'public' | 'unlisted';
    onVisibilityChange?: (visibility: 'private' | 'public') => void;
    isUpdatingVisibility?: boolean;
    includeAdjacent?: boolean;
    onIncludeAdjacentChange?: (value: boolean) => void;
    isUpdatingIncludeAdjacent?: boolean;
    isIncludeAdjacentDisabled?: boolean;
    statusOverride?: {
        label: string;
        tone: 'active' | 'paused' | 'draft';
    };
}

export default function ProjectDetailsCard({
    style,
    mode = 'configuration',
    visibility,
    onVisibilityChange,
    isUpdatingVisibility,
    includeAdjacent,
    onIncludeAdjacentChange,
    isUpdatingIncludeAdjacent,
    isIncludeAdjacentDisabled = false,
    statusOverride,
}: ProjectDetailsCardProps) {
    const { project, isLoading } = useProject();
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const isDescriptionMode = mode === 'description';

    // Extract categories and keywords for display
    const categories = useMemo(() => {
        if (!project?.project_category_interests) return [];
        return project.project_category_interests
            .map(pci => pci.category)
            .filter(Boolean) as Array<Category>;
    }, [project?.project_category_interests]);

    const includeKeywords = useMemo(() => {
        if (!project?.project_keywords) return [];
        return project.project_keywords
            .filter(k => !k.is_exclusion)
            .map(k => k.keyword);
    }, [project?.project_keywords]);

    const excludeKeywords = useMemo(() => {
        if (!project?.project_keywords) return [];
        return project.project_keywords
            .filter(k => k.is_exclusion)
            .map(k => k.keyword);
    }, [project?.project_keywords]);

    return (
        <View
            style={[styles.card, style]}
            testID={isDescriptionMode ? 'project-description-card' : 'project-configuration-card'}
        >
            {/* Show loading state while fetching project data */}
            {isLoading && !project && (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="small" color={colors.primaryGreen} />
                    <Text style={styles.loadingText}><Trans id="projectdetailscard.loadingProjectDetails" comment="UI copy in components/ProjectDetailsCard.tsx">Loading project details...</Trans></Text>
                </View>
            )}

            {/* Show content when project data is available */}
            {project && (
                <>
                    {/* Header with status badge */}
                    <View style={styles.header}>
                        <View style={styles.headerLeft}>
                            <View style={styles.headerIconContainer}>
                                <FontAwesome6
                                    name={isDescriptionMode ? 'file-lines' : 'sliders'}
                                    size={16}
                                    color={colors.text700}
                                />
                            </View>
                            <Text style={styles.headerTitle}>
                                {isDescriptionMode ? <Trans id="projectdetailscard.description" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Description</Trans> : <Trans id="projectdetailscard.configuration" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Configuration</Trans>}
                            </Text>
                        </View>
                        {!isDescriptionMode && (() => {
                            const computedTone = project.paused_at ? 'paused' : isProjectActivated(project) ? 'active' : 'draft';
                            const status = statusOverride?.label ?? i18n._(computedTone === 'active'
                                ? msg({ id: 'project.status.active', message: 'Active' })
                                : computedTone === 'paused'
                                    ? msg({ id: 'project.status.paused', message: 'Paused' })
                                    : msg({ id: 'project.status.setupNeeded', message: 'Setup needed' }));
                            const statusTone = statusOverride?.tone ?? computedTone;

                            return (
                                <View style={[
                                    styles.statusBadge,
                                    statusTone === 'active' ? styles.statusBadgeActive :
                                    statusTone === 'paused' ? styles.statusBadgePaused :
                                    styles.statusBadgeDraft
                                ]}>
                                    <Text style={[
                                        styles.statusBadgeText,
                                        statusTone === 'active' ? styles.statusBadgeTextActive :
                                        statusTone === 'paused' ? styles.statusBadgeTextPaused :
                                        styles.statusBadgeTextDraft
                                    ]}>
                                        {status}
                                    </Text>
                                </View>
                            );
                        })()}
                    </View>

                    {/* Stats row */}
                    {!isDescriptionMode && <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <FontAwesome6 name="clock" size={13} color={colors.text500} style={styles.statIcon} />
                            <Text style={styles.statLabel}><Trans id="projectdetailscard.frequency" comment="UI copy in components/ProjectDetailsCard.tsx">Frequency</Trans></Text>
                            <Text style={[styles.statValue, project.email_frequency === 'pause' && styles.statValuePaused]}>
                                {project.email_frequency === 'pause' ? <Trans id="projectdetailscard.paused" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Paused</Trans> : project.email_frequency === 'daily' ? <Trans id="projectdetailscard.daily" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Daily</Trans> : <Trans id="projectdetailscard.weekly" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Weekly</Trans>}
                            </Text>
                        </View>
                        <View style={styles.statItem}>
                            <FontAwesome6 name="file-lines" size={13} color={colors.text500} style={styles.statIcon} />
                            <Text style={styles.statLabel}><Trans id="projectdetailscard.maxPapers" comment="UI copy in components/ProjectDetailsCard.tsx">Max papers</Trans></Text>
                            <Text style={styles.statValue}>{project.max_papers_per_digests || 10}</Text>
                        </View>
                    </View>}

                    {/* Categories */}
                    {categories.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.sectionLabel}><Trans id="projectdetailscard.categories" comment="UI copy in components/ProjectDetailsCard.tsx">Categories</Trans></Text>
                            <View style={styles.categoriesContainer}>
                                {categories.map((category, index) => {
                                    const sourceLogo = category.oai_spec?.startsWith('iacr:')
                                        ? getSourceLogoByName('iacr') : getSourceLogoById(category.source_id);
                                    return (
                                        <View key={`cat-${index}`} style={styles.categoryChip}>
                                            {sourceLogo !== null ? (
                                                <Image
                                                    source={sourceLogo}
                                                    style={styles.categorySourceIcon}
                                                    resizeMode="contain"
                                                />
                                            ) : (
                                                <FontAwesome6 name="folder" size={11} color={colors.text600} style={styles.categoryIcon} />
                                            )}
                                            <Text style={styles.categoryText}>{category.name || category.code}</Text>
                                        </View>
                                    );
                                })}
                            </View>
                        </View>
                    )}

                    {/* Interests */}
                    {project.interest_description && (
                        <View style={styles.section}>
                            <Text style={styles.sectionLabel}>
                                {isDescriptionMode ? <Trans id="projectdetailscard.researchFocus" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Research focus</Trans> : <Trans id="projectdetailscard.interests" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Interests</Trans>}
                            </Text>
                            <Text style={styles.sectionValue}>{project.interest_description}</Text>
                        </View>
                    )}

                    {/* Keywords */}
                    {(includeKeywords.length > 0 || excludeKeywords.length > 0) && (
                        <View style={styles.section}>
                            <Text style={styles.sectionLabel}><Trans id="projectdetailscard.keywords" comment="UI copy in components/ProjectDetailsCard.tsx">Keywords</Trans></Text>
                            <View style={styles.keywordsContainer}>
                                {includeKeywords.map((keyword, index) => (
                                    <View key={`include-${index}`} style={styles.keywordChipInclude}>
                                        <Text style={styles.keywordTextInclude}>+{keyword}</Text>
                                    </View>
                                ))}
                                {excludeKeywords.map((keyword, index) => (
                                    <View key={`exclude-${index}`} style={styles.keywordChipExclude}>
                                        <Text style={styles.keywordTextExclude}>-{keyword}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    )}

                    {isDescriptionMode && (
                        <View style={styles.descriptionNote}>
                            <FontAwesome6 name="pen" size={11} color={colors.text500} />
                            <Text
                                style={styles.descriptionNoteText}
                                testID="project-description-change-note"
                            ><Trans id="projectdetailscard.youCanChangeTheCategoriesDescriptionAndKeywordsAtAny" comment="UI copy in components/ProjectDetailsCard.tsx">
                                You can change the categories, description, and keywords at any time.
                            </Trans></Text>
                        </View>
                    )}

                    {/* Visibility toggle */}
                    {!isDescriptionMode && visibility !== undefined && onVisibilityChange && (
                        <View style={styles.visibilitySection}>
                            <View style={styles.visibilityRow}>
                                <View style={styles.visibilityLeft}>
                                    <View style={styles.visibilityMeta}>
                                        <Text style={styles.visibilityLabel}><Trans id="projectdetailscard.visibility" comment="UI copy in components/ProjectDetailsCard.tsx">Visibility:</Trans></Text>
                                        <Text style={styles.visibilityValue}>{i18n._(visibility === 'public'
                                            ? msg({ id: 'project.visibility.public', message: 'Public' })
                                            : visibility === 'unlisted'
                                                ? msg({ id: 'project.visibility.unlisted', message: 'Unlisted' })
                                                : msg({ id: 'project.visibility.private', message: 'Private' }))}</Text>
                                    </View>
                                    <View style={styles.visibilityHintRow}>
                                        <Text style={styles.visibilityHintText}><Trans id="projectdetailscard.publicProjectsShowUpIn" comment="UI copy in components/ProjectDetailsCard.tsx">Public projects show up in </Trans></Text>
                                        <Link href="/public-digests" asChild>
                                            <Text style={styles.visibilityHintLink}><Trans id="projectdetailscard.publicDigests" comment="UI copy in components/ProjectDetailsCard.tsx">Public Digests</Trans></Text>
                                        </Link>
                                    </View>
                                </View>
                                <Switch
                                    value={visibility === 'public'}
                                    onValueChange={(value) => onVisibilityChange(value ? 'public' : 'private')}
                                    disabled={isUpdatingVisibility}
                                    trackColor={{ false: '#d1d5db', true: colors.primaryGreen }}
                                    thumbColor={'#ffffff'}
                                />
                            </View>
                        </View>
                    )}

                    {/* Include adjacent papers toggle */}
                    {!isDescriptionMode && includeAdjacent !== undefined && onIncludeAdjacentChange && (
                        <View style={styles.adjacentSection}>
                            <View style={styles.visibilityRow}>
                                <View style={styles.adjacentLeft}>
                                    <View style={styles.visibilityMeta}>
                                        <Text style={styles.visibilityLabel}><Trans id="projectdetailscard.includeAdjacentPapers" comment="UI copy in components/ProjectDetailsCard.tsx">Include adjacent papers:</Trans></Text>
                                        <Text style={styles.visibilityValue}>{includeAdjacent ? <Trans id="projectdetailscard.yes" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">yes</Trans> : <Trans id="projectdetailscard.no" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">no</Trans>}</Text>
                                    </View>
                                    <Text style={styles.adjacentDescription}>
                                        {isIncludeAdjacentDisabled
                                            ? <Trans id="projectdetailscard.availableAfterTheFirstProcessingPassFinishes" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Available after the first processing pass finishes</Trans>
                                            : <Trans id="projectdetailscard.showRelatedPapersThatArenTDirectMatches" comment="Conditional UI copy in components/ProjectDetailsCard.tsx">Show related papers that aren't direct matches</Trans>}
                                    </Text>
                                </View>
                                <Switch
                                    value={includeAdjacent}
                                    onValueChange={onIncludeAdjacentChange}
                                    disabled={isUpdatingIncludeAdjacent || isIncludeAdjacentDisabled}
                                    trackColor={{ false: '#d1d5db', true: colors.primaryGreen }}
                                    thumbColor={'#ffffff'}
                                    testID="project-details-include-adjacent-switch"
                                />
                            </View>
                        </View>
                    )}

                    {/* Created date */}
                    {!isDescriptionMode && project.created_at && (
                        <View style={styles.footer}>
                            <Text style={styles.footerText}><Trans id="projectdetailscard.created" comment="UI copy in components/ProjectDetailsCard.tsx">
                                Created: {new Date(project.created_at).toLocaleDateString(formatLocale, {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric'
                                })}
                            </Trans></Text>
                        </View>
                    )}
                </>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: colors.card,
        padding: 24,
        borderRadius: 16,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOpacity: 0.04,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 4 },
            elevation: 2,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
        }),
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    headerIconContainer: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.bgWeak,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        ...fonts.bodyMedium,
        fontWeight: '600',
        color: colors.text900,
        letterSpacing: 0.2,
    },
    statusBadge: {
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderRadius: 20,
    },
    statusBadgeActive: {
        backgroundColor: colors.primary100,
    },
    statusBadgeDraft: {
        backgroundColor: colors.bgWeak,
    },
    statusBadgePaused: {
        backgroundColor: '#fef3c7',
    },
    statusBadgeText: {
        ...fonts.label,
        fontWeight: '600',
    },
    statusBadgeTextActive: {
        color: colors.primaryGreen,
    },
    statusBadgeTextDraft: {
        color: colors.text600,
    },
    statusBadgeTextPaused: {
        color: '#d97706',
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 32,
    },
    loadingText: {
        ...fonts.bodySmall,
        color: colors.text600,
        marginLeft: 12,
    },
    statsRow: {
        flexDirection: 'row',
        gap: 24,
        marginBottom: 24,
        paddingBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        justifyContent: 'flex-start',
    },
    statItem: {
        alignItems: 'center',
        gap: 4,
        minWidth: 80,
    },
    statIcon: {
        marginBottom: 4,
    },
    statLabel: {
        ...fonts.label,
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    statValue: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: colors.text900,
        textAlign: 'center',
    },
    statValuePaused: {
        color: '#d97706',
    },
    section: {
        marginBottom: 20,
    },
    categoriesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    categoryChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 7,
        borderRadius: 8,
        backgroundColor: colors.bgWeak,
        gap: 6,
        maxWidth: '100%',
        flexShrink: 1,
    },
    categoryIcon: {
        opacity: 0.7,
        flexShrink: 0,
    },
    categorySourceIcon: {
        width: 12,
        height: 12,
        flexShrink: 0,
    },
    categoryText: {
        ...fonts.label,
        fontWeight: '500',
        color: colors.text700,
        flexShrink: 1,
    },
    sectionLabel: {
        ...fonts.label,
        fontWeight: '600',
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    sectionValue: {
        ...fonts.bodySmall,
        color: colors.text700,
        lineHeight: 22,
    },
    keywordsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    keywordChipInclude: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: colors.primary100,
    },
    keywordTextInclude: {
        ...fonts.label,
        fontWeight: '500',
        color: colors.primaryGreen,
    },
    keywordChipExclude: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: colors.text900,
    },
    keywordTextExclude: {
        ...fonts.label,
        fontWeight: '500',
        color: '#ffffff',
    },
    descriptionNote: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    descriptionNoteText: {
        ...fonts.label,
        color: colors.text500,
    },
    visibilitySection: {
        marginTop: 4,
        paddingTop: 20,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    visibilityRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    visibilityLeft: {
        flex: 1,
        gap: 4,
    },
    visibilityMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    visibilityLabel: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: colors.text900,
    },
    visibilityValue: {
        ...fonts.bodySmall,
        color: colors.text600,
        textTransform: 'capitalize',
    },
    visibilityHintRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    visibilityHintText: {
        ...fonts.label,
        color: colors.text500,
    },
    visibilityHintLink: {
        ...fonts.label,
        color: colors.primaryGreen,
        textDecorationLine: 'underline',
    },
    adjacentSection: {
        marginTop: 4,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    adjacentLeft: {
        flex: 1,
        gap: 4,
    },
    adjacentDescription: {
        ...fonts.label,
        color: colors.text500,
    },
    footer: {
        marginTop: 16,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    footerText: {
        ...fonts.label,
        color: colors.text500,
    },
});
